import type { IncidentGroup } from '../../services/incidentsApi';
import { resolveIncidentGroupActionTarget } from '../../utils/incidentGroupKey';
import type { IncidentTimeRange } from '../../pages/incidents/useIncidentsTimeRange';

export const PATTERN_ACTION_SELECTION_SET_TITLE = 'Selection set';
export const PATTERN_ACTION_SELECTION_SET_EXACT_INTRO =
  'This is the exact set the action will update.';
export const PATTERN_ACTION_SELECTION_SET_SCOPE_INTRO =
  'This action applies to matching open linked records in the current review window.';
export const PATTERN_ACTION_SELECTION_SET_TOGGLE_EXPAND = 'Show matching criteria';
export const PATTERN_ACTION_SELECTION_SET_TOGGLE_COLLAPSE = 'Hide matching criteria';

export type PatternActionViewScope = {
  groupKey: string;
  fromDate: string;
  toDate: string;
  statusFilter: 'OPEN';
  incidentType?: string;
  zoneId?: string;
  cameraId?: string;
  deviceId?: string;
};

export type PatternActionSelectionSetField = {
  label: string;
  value: string;
};

export type PatternActionSelectionSetModel = {
  exactIntro: string;
  scopeIntro: string;
  compactSummary: string | null;
  fields: PatternActionSelectionSetField[];
  viewScope: PatternActionViewScope | null;
};

function displayOrUnknown(value: unknown, unknownLabel: string): string {
  const text = String(value ?? '').trim();
  return text || unknownLabel;
}

export function formatPatternActionTimeWindow(
  fromDate?: string,
  toDate?: string,
): string {
  if (!fromDate || !toDate) return 'Set review window (from / to)';
  const formatInstant = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
  };
  return `${formatInstant(fromDate)} → ${formatInstant(toDate)}`;
}

export function formatPatternActionAffectedOccurrences(
  matchedCount: number | null | undefined,
  loading?: boolean,
): string {
  if (loading) return 'Resolving…';
  if (matchedCount == null) return 'Refresh count to resolve';
  const n = Math.max(0, Number(matchedCount) || 0);
  return `${n.toLocaleString()} open linked record${n === 1 ? '' : 's'}`;
}

export function buildPatternActionSelectionSetCompactSummary(args: {
  matchedCount: number | null | undefined;
  previewLoading?: boolean;
  zoneLabel?: string;
  cameraLabel?: string;
  deviceId?: string;
  timeRangeLabel?: string;
}): string | null {
  const { matchedCount, previewLoading, zoneLabel, cameraLabel, deviceId, timeRangeLabel } = args;
  if (previewLoading || matchedCount == null) return null;
  const n = Math.max(0, Number(matchedCount) || 0);
  const location = [zoneLabel, cameraLabel, deviceId].filter((part) => String(part || '').trim()).join(' · ')
    || 'location unknown';
  const windowPhrase = timeRangeLabel || 'selected window';
  return `Selection set: ${n.toLocaleString()} OPEN occurrences · ${location} · ${windowPhrase}`;
}

export function buildPatternActionViewScope(
  group: IncidentGroup | null,
  timeRange: IncidentTimeRange,
): PatternActionViewScope | null {
  if (!group || !timeRange.fromDate || !timeRange.toDate) return null;
  const { groupKey } = resolveIncidentGroupActionTarget(group);
  if (!groupKey) return null;
  return {
    groupKey,
    fromDate: timeRange.fromDate,
    toDate: timeRange.toDate,
    statusFilter: 'OPEN',
    incidentType: String(group.incident_type || '').trim() || undefined,
    zoneId: String(group.zone_id || '').trim() || undefined,
    cameraId: String(group.camera_id || '').trim() || undefined,
    deviceId: String(group.device_id || '').trim() || undefined,
  };
}

export function buildPatternActionSelectionSetModel(args: {
  group: IncidentGroup | null;
  timeRange: IncidentTimeRange;
  matchedCount?: number | null;
  previewLoading?: boolean;
  patternLabel?: string;
  zoneLabel?: string;
  cameraLabel?: string;
}): PatternActionSelectionSetModel {
  const { group, timeRange, matchedCount, previewLoading, patternLabel, zoneLabel, cameraLabel } = args;
  const { groupKey } = group ? resolveIncidentGroupActionTarget(group) : { groupKey: '' };
  const pattern =
    displayOrUnknown(
      patternLabel
      || group?.incident_type
      || (group as any)?.summary_text,
      'Unknown recurring risk',
    );
  const zone = displayOrUnknown(zoneLabel || group?.zone_id, 'Unknown zone');
  const camera = displayOrUnknown(cameraLabel || group?.camera_id, 'Unknown camera');
  const device = displayOrUnknown(group?.device_id, 'Unknown device');
  const timeWindow = formatPatternActionTimeWindow(timeRange.fromDate, timeRange.toDate);

  return {
    exactIntro: PATTERN_ACTION_SELECTION_SET_EXACT_INTRO,
    scopeIntro: PATTERN_ACTION_SELECTION_SET_SCOPE_INTRO,
    compactSummary: buildPatternActionSelectionSetCompactSummary({
      matchedCount,
      previewLoading,
      zoneLabel: zone,
      cameraLabel: camera,
      deviceId: device,
      timeRangeLabel: timeRange.label || 'selected window',
    }),
    fields: [
      { label: 'Recurring risk', value: pattern },
      { label: 'Group key', value: displayOrUnknown(groupKey, 'Unknown group key') },
      { label: 'Zone', value: zone },
      { label: 'Camera', value: camera },
      { label: 'Device', value: device },
      { label: 'Status', value: 'OPEN only' },
      { label: 'Time window', value: timeWindow },
      {
        label: 'Affected occurrences',
        value: formatPatternActionAffectedOccurrences(matchedCount, previewLoading),
      },
    ],
    viewScope: buildPatternActionViewScope(group, timeRange),
  };
}
